package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ThemeMetadataAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeTokenAst

/**
 * 主题预设注册表：scheme 名 → token 表。
 *
 * 与 [ComponentRegistry] 并列，由 [ThemeCollectorPass] 在编译期填充。
 * screen 内 `theme { scheme = "<name>" }` 引用预设时，此表中的 tokens 作为 baseline，
 * screen 显式声明字段在合并时覆盖。
 *
 * 同时保留：
 * - tokenNodes：定义节点，供继承链 / 诊断使用实际源码位置
 * - metadataByScheme：目录展示元数据，不进入 ThemeLookup
 */
class ThemeRegistry {

    private val schemes = LinkedHashMap<String, LinkedHashMap<String, Any>>()
    private val tokenNodes = LinkedHashMap<String, LinkedHashMap<String, ThemeTokenAst>>()
    private val metadataByScheme = LinkedHashMap<String, ThemeMetadataAst?>()

    /** 注册或更新一个 scheme；更新 token 时默认保留其独立 metadata。 */
    fun register(
        scheme: String,
        tokens: LinkedHashMap<String, Any>,
        metadata: ThemeMetadataAst? = metadataByScheme[scheme],
    ) {
        schemes[scheme] = tokens
        if (scheme !in tokenNodes) tokenNodes[scheme] = linkedMapOf()
        metadataByScheme[scheme] = metadata
    }

    /** 连同 token 定义节点注册，供解析与继承链保留实际位置。 */
    internal fun registerWithNodes(
        scheme: String,
        tokens: LinkedHashMap<String, Any>,
        nodes: Map<String, ThemeTokenAst>,
        metadata: ThemeMetadataAst? = metadataByScheme[scheme],
    ) {
        schemes[scheme] = tokens
        tokenNodes[scheme] = LinkedHashMap(nodes)
        metadataByScheme[scheme] = metadata
    }

    /** token 查询；metadata 不通过此入口暴露，避免进入 ThemeLookup。 */
    fun lookup(scheme: String): Map<String, Any>? = schemes[scheme]

    /** 查询 token 定义节点；继承与解析诊断使用实际定义位置。 */
    internal fun tokenNodes(scheme: String): Map<String, ThemeTokenAst> = tokenNodes[scheme].orEmpty()

    /** 独立查询目录 metadata；未声明或未命中均返回 null。 */
    fun metadata(scheme: String): ThemeMetadataAst? = metadataByScheme[scheme]

    /** 已注册的 scheme 名列表（保持插入顺序）。 */
    fun schemes(): Set<String> = schemes.keys
}
